import { useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Send, Pencil, X, MessageSquareHeart, Smile } from 'lucide-react';
import { api } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

// Lazy-load emoji-mart (~150 KB gz) so the rest of the dashboard isn't
// paying for it. Module-level promises cache the result across mounts.
let emojiPickerPromise = null;
let emojiDataPromise = null;
function loadEmojiPicker() {
  if (!emojiPickerPromise) emojiPickerPromise = import('@emoji-mart/react').then(m => m.default);
  if (!emojiDataPromise)   emojiDataPromise   = import('@emoji-mart/data').then(m => m.default);
  return Promise.all([emojiPickerPromise, emojiDataPromise]);
}

function hexFromColor(int) {
  if (!int) return null;
  return '#' + int.toString(16).padStart(6, '0');
}

const EMPTY_OPTION = () => ({ emoji: '', role_id: '', label: '' });

export default function ConfigReactionRoles() {
  const { guildId, channels } = useGuild();
  const [list, setList] = useState(null);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => { reload(); api.guildRoles(guildId).then(r => setRoles(r.roles)); }, [guildId]);

  function reload() {
    setList(null);
    api.listReactionRoles(guildId).then(d => setList(d.messages)).catch(err => setList({ error: err.message }));
  }

  function fireToast(type, msg) {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  }

  async function handleSave(form) {
    try {
      if (form.id) await api.updateReactionRole(guildId, form.id, form);
      else await api.createReactionRole(guildId, form);
      setEditing(null);
      reload();
      fireToast('success', 'Saved');
    } catch (err) { fireToast('error', err.message); }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this reaction-role message? The posted Discord message will also be deleted.')) return;
    try { await api.deleteReactionRole(guildId, id); reload(); fireToast('success', 'Deleted'); }
    catch (err) { fireToast('error', err.message); }
  }

  async function handlePublish(id) {
    try { await api.publishReactionRole(guildId, id); reload(); fireToast('success', 'Published'); }
    catch (err) { fireToast('error', err.message); }
  }

  if (list === null) return <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>;
  if (list.error) return <div className="empty">Error: {list.error}</div>;

  return (
    <>
      <h2 style={{ marginTop: 0 }}>Reaction roles</h2>
      <p className="muted" style={{ marginTop: '-.25rem' }}>Members react with an emoji to self-assign a role. Great for game-ping opt-ins.</p>

      <div className="row actions" style={{ marginBottom: '1rem' }}>
        <button onClick={() => setEditing({ channel_id: '', title: '', description: '', options: [EMPTY_OPTION()] })}>
          <Plus size={14} strokeWidth={2.5} /> New reaction-role message
        </button>
      </div>

      {!list.length ? (
        <div className="card empty">
          <div className="ico"><MessageSquareHeart size={32} strokeWidth={1.5} /></div>
          <p>No reaction-role messages yet. Click "New" above to build one.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
          {list.map(m => {
            const channel = channels.find(c => c.id === m.channel_id);
            return (
              <div key={m.id} className="card">
                <div className="row" style={{ alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{m.title || '(untitled)'}</div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                      {channel ? `#${channel.name}` : <span className="dim">channel missing</span>}
                      {' · '}
                      {m.message_id
                        ? <span className="badge success">published</span>
                        : <span className="badge warn">draft</span>}
                      {' · '}{m.options.length} option{m.options.length === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="row" style={{ flex: 0, gap: '.4rem' }}>
                    <button className="ghost" onClick={() => handlePublish(m.id)} title={m.message_id ? 'Republish (edits in place)' : 'Publish to channel'}>
                      <Send size={14} strokeWidth={2.5} /> {m.message_id ? 'Republish' : 'Publish'}
                    </button>
                    <button className="ghost" onClick={() => setEditing({ ...m })}><Pencil size={14} strokeWidth={2.5} /> Edit</button>
                    <button className="ghost danger-text" onClick={() => handleDelete(m.id)}><Trash2 size={14} strokeWidth={2.5} /></button>
                  </div>
                </div>
                {m.options.length > 0 && (
                  <div style={{ marginTop: '.75rem', display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {m.options.map(o => {
                      const role = roles.find(r => r.id === o.role_id);
                      const color = role ? hexFromColor(role.color) : null;
                      return (
                        <div key={o.id} className="muted" style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                          <span style={{ fontSize: 16 }}>{o.emoji}</span>
                          <span style={{ color: color || 'inherit', fontWeight: 600 }}>{role ? role.name : `<unknown role ${o.role_id}>`}</span>
                          {o.label && <span className="dim">— {o.label}</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <EditorModal
          initial={editing}
          channels={channels}
          roles={roles}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

function EditorModal({ initial, channels, roles, onCancel, onSave }) {
  const [form, setForm] = useState({
    id: initial.id,
    channel_id: initial.channel_id || '',
    title: initial.title || '',
    description: initial.description || '',
    options: (initial.options && initial.options.length ? initial.options : [EMPTY_OPTION()]).map(o => ({
      emoji: o.emoji || '', role_id: o.role_id || '', label: o.label || '',
    })),
  });
  const [pickerFor, setPickerFor] = useState(null);
  const [emojiBits, setEmojiBits] = useState({ Picker: null, data: null });

  function togglePicker(i) {
    if (!emojiBits.Picker) {
      loadEmojiPicker().then(([Picker, data]) => setEmojiBits({ Picker, data }));
    }
    setPickerFor(prev => (prev === i ? null : i));
  }

  function updateOption(i, field, value) {
    setForm(f => ({
      ...f,
      options: f.options.map((o, idx) => idx === i ? { ...o, [field]: value } : o),
    }));
  }
  function addOption() {
    setForm(f => ({ ...f, options: [...f.options, EMPTY_OPTION()] }));
  }
  function removeOption(i) {
    setForm(f => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));
  }
  function moveOption(i, delta) {
    setForm(f => {
      const next = [...f.options];
      const j = i + delta;
      if (j < 0 || j >= next.length) return f;
      [next[i], next[j]] = [next[j], next[i]];
      return { ...f, options: next };
    });
  }

  const canSave = form.channel_id && form.options.length > 0 && form.options.every(o => o.emoji && o.role_id);

  function submit(e) {
    e.preventDefault();
    if (!canSave) return;
    onSave(form);
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ minWidth: 540, maxWidth: 640 }}>
        <h3 style={{ marginTop: 0 }}>
          {form.id ? 'Edit reaction-role message' : 'New reaction-role message'}
          <button className="ghost" style={{ float: 'right', padding: 4 }} onClick={onCancel}><X size={16} strokeWidth={2.5} /></button>
        </h3>
        <form onSubmit={submit}>
          <div className="field">
            <label>Channel</label>
            <select value={form.channel_id} onChange={e => setForm(f => ({ ...f, channel_id: e.target.value }))} required>
              <option value="">— pick a channel —</option>
              {channels.filter(c => c.type === 0 || c.type === 5).map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Title</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Game ping roles" />
          </div>
          <div className="field">
            <label>Description (optional)</label>
            <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="React below to get pinged when we're playing." />
          </div>

          <label>Options</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem', marginBottom: '.75rem' }}>
            {form.options.map((o, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '100px 1fr 1fr auto auto', gap: '.4rem', alignItems: 'center' }}>
                <div style={{ position: 'relative', display: 'flex', gap: 2 }}>
                  <input
                    value={o.emoji}
                    onChange={e => updateOption(i, 'emoji', e.target.value)}
                    placeholder="emoji"
                    style={{ textAlign: 'center', fontSize: 18, padding: '.45rem .4rem' }}
                    required
                  />
                  <button
                    type="button"
                    className="ghost"
                    onClick={() => togglePicker(i)}
                    title="Pick from full emoji set"
                    style={{ padding: '.45rem .55rem', flex: 0 }}
                  >
                    <Smile size={14} strokeWidth={2.5} />
                  </button>
                  {pickerFor === i && (() => {
                    const EmojiPicker = emojiBits.Picker;
                    if (!EmojiPicker || !emojiBits.data) {
                      return (
                        <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000, padding: '.75rem 1rem', background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 8 }}>
                          <span className="dim" style={{ fontSize: 12 }}>Loading emoji picker…</span>
                        </div>
                      );
                    }
                    return (
                      <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 1000 }}>
                        <EmojiPicker
                          data={emojiBits.data}
                          theme="dark"
                          previewPosition="none"
                          skinTonePosition="none"
                          navPosition="bottom"
                          autoFocus
                          onEmojiSelect={(em) => {
                            updateOption(i, 'emoji', em.native || em.shortcodes || '');
                            setPickerFor(null);
                          }}
                          onClickOutside={() => setPickerFor(null)}
                        />
                      </div>
                    );
                  })()}
                </div>
                <select value={o.role_id} onChange={e => updateOption(i, 'role_id', e.target.value)} required>
                  <option value="">— role —</option>
                  {roles.map(r => <option key={r.id} value={r.id} disabled={!r.assignable}>
                    {r.name}{!r.assignable ? ' (too high)' : ''}
                  </option>)}
                </select>
                <input
                  value={o.label}
                  onChange={e => updateOption(i, 'label', e.target.value)}
                  placeholder="Optional label"
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <button type="button" className="ghost" style={{ padding: '2px 6px', fontSize: 11, lineHeight: 1 }} onClick={() => moveOption(i, -1)} disabled={i === 0}>▲</button>
                  <button type="button" className="ghost" style={{ padding: '2px 6px', fontSize: 11, lineHeight: 1 }} onClick={() => moveOption(i, 1)} disabled={i === form.options.length - 1}>▼</button>
                </div>
                <button type="button" className="ghost" style={{ padding: '.4rem .55rem' }} onClick={() => removeOption(i)} disabled={form.options.length === 1}>
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" className="ghost" onClick={addOption}><Plus size={14} strokeWidth={2.5} /> Add option</button>
          <p className="dim" style={{ fontSize: 12, marginTop: 8 }}>
            Emoji: paste a Unicode emoji (🎮) or a custom one in the format <span className="tag">&lt;:name:1234567890&gt;</span>. Role: must be below SoupOS in the role list.
          </p>

          <div className="row actions" style={{ gap: '.5rem', marginTop: '1.25rem' }}>
            <button type="button" className="ghost" onClick={onCancel}>Cancel</button>
            <button type="submit" disabled={!canSave}>Save</button>
          </div>
        </form>
      </div>
    </div>
  );
}
